import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { Autocomplete, Box, Paper, TextField, Typography } from '@mui/material';
import { styled, darken } from '@mui/system';
import { Organization } from '@/types/organization';
import toast from 'react-hot-toast';
import { useIntegrationsData } from '@/contexts/IntegrationsContext';
import { utils } from '../../utils';
import { useCommittedOrganization } from '@/session/OrganizationIdentityContext';
import { acquireOperationContext } from '@/session/operation';
import { setLastVisitedOrganization } from '@/session/lastVisitedOrganization';

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.mode === 'light' ? darken(theme.palette.primary.light, 0.7) : darken(theme.palette.primary.main, 0.7),
}));

const GroupItems = styled('ul')(({ theme }) => ({
  padding: 0,
  color: theme.palette.mode === 'light' ? theme.palette.primary.light : theme.palette.primary.main,
  backgroundColor: theme.palette.mode === 'light' ? darken(theme.palette.primary.light, 0.8) : darken(theme.palette.primary.main, 0.8),
}));

type FormattedOption = {
  title: string;
  value: string;
  name: string;
  firstLetter: string;
};

type OrganizationSelectorProps = {
  organizations?: Organization[];
  onOrganizationChange?: (organizationName: string) => Promise<void>;
};

export const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({ organizations = [], onOrganizationChange }) => {
  const router = useRouter();
  const { t } = useTranslation();
  const theme = useTheme();
  const currentOrganization = useCommittedOrganization();
  const isStaff = utils.authUtils.isAcendaStaff();

  // Handle organization switching
  const { resetIntegrationsData } = useIntegrationsData();
  const handleOrganizationSwitch = async (organizationName: string): Promise<void> => {
    const operation = acquireOperationContext(currentOrganization);
    try {
      if (!operation.isScopeCurrent()) return;
      const navigated = await router.push('/' + organizationName);
      // A page's leave guard kept the user there, or a newer navigation took over: nothing failed.
      if (!navigated) return;

      setLastVisitedOrganization(organizationName, operation.subject);
      resetIntegrationsData();

      // Call the callback if provided
      if (operation.isScopeCurrent() && onOrganizationChange) {
        await onOrganizationChange(organizationName);
      }
    } catch (error) {
      if (operation.isScopeCurrent()) toast.error(t('Failed to switch organization'));
    }
  };

  const formattedOptions: FormattedOption[] = organizations.map((org) => {
    const title = org.title || org.name;
    const firstChar = title[0].toUpperCase();
    return {
      title,
      value: org.name,
      name: org.name,
      firstLetter: /[0-9]/.test(firstChar) ? '0-9' : firstChar,
    };
  });

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        id='organization-selector'
        disablePortal
        value={{
          title: String(currentOrganization),
          value: String(currentOrganization),
          name: String(currentOrganization),
          firstLetter: currentOrganization && currentOrganization.length > 0 ? currentOrganization[0].toUpperCase() : '',
        }}
        options={formattedOptions.sort((a, b) => {
          // Sort by letter group and then alphabetically
          const letterCompare = a.firstLetter.localeCompare(b.firstLetter);
          if (letterCompare !== 0) {
            return letterCompare;
          }
          return a.title.localeCompare(b.title);
        })}
        groupBy={(option) => option.firstLetter}
        getOptionLabel={(option) => option.title || ''}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(event, newValue) => {
          if (newValue) {
            void handleOrganizationSwitch(newValue.value);
          }
        }}
        filterOptions={(options, params) => {
          const filtered = options.filter((option) => {
            if (!params.inputValue) {
              return true;
            }
            return (
              option.title.toLowerCase().includes(params.inputValue.toLowerCase()) ||
              option.value.toLowerCase().includes(params.inputValue.toLowerCase()) ||
              option.name.toLowerCase().includes(params.inputValue.toLowerCase())
            );
          });
          return filtered;
        }}
        sx={{ border: '0px', p: 0, width: '100%' }}
        PaperComponent={({ children }) => (
          <Paper
            style={{
              background: theme.palette.mode === 'light' ? darken(theme.palette.primary.light, 0.8) : darken(theme.palette.primary.main, 0.8),
            }}
          >
            {children}
          </Paper>
        )}
        renderInput={(params) => (
          <TextField
            sx={{
              border: 'solid black 0px',
              '& .MuiInputLabel-root': { color: theme.palette.primary.light },
              '& .MuiOutlinedInput-root': {
                '& > fieldset': { border: '0px', borderColor: 'black' },
              },
              input: { color: 'white' },
              backgroundColor: theme.palette.mode === 'light' ? darken(theme.palette.primary.light, 0.8) : darken(theme.palette.primary.main, 0.8),
            }}
            {...params}
            label={t('Organization')}
          />
        )}
        renderGroup={(params) => (
          <li key={params.group}>
            {formattedOptions.length > 10 && <GroupHeader>{params.group}</GroupHeader>}
            <GroupItems>{params.children}</GroupItems>
          </li>
        )}
      />

      {/* User role/tier display */}
      <Typography color='neutral.400' variant='body2' style={{ marginLeft: '1em', marginTop: '0.5em' }}>
        {isStaff ? (
          <>
            {t('User Role')} : {utils.stringUtils.titleCase(utils.authUtils.acendaStaffRolePretty() || '')}
          </>
        ) : (
          <>{t('Your tier')} : Premium</>
        )}
      </Typography>
    </Box>
  );
};
